'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type WorkspaceRole = 'admin' | 'delivery_lead' | 'pmo' | 'viewer' | null;

export interface WorkspacePermissions {
  role: WorkspaceRole;
  canEdit: boolean;       // admin, delivery_lead
  canManageTeam: boolean; // admin only
  canViewAll: boolean;    // all roles
  canUpdateProject: boolean; // admin, delivery_lead, pmo
  isLoading: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  delivery_lead: 'Delivery Lead',
  pmo: 'PMO',
  viewer: 'Viewer',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-50 text-purple-700 border-purple-200',
  delivery_lead: 'bg-blue-50 text-blue-700 border-blue-200',
  pmo: 'bg-amber-50 text-amber-700 border-amber-200',
  viewer: 'bg-slate-50 text-slate-600 border-slate-200',
};

export function getRoleLabel(role: WorkspaceRole): string {
  if (!role) return 'No Access';
  return ROLE_LABELS[role] || role;
}

export function getRoleColor(role: WorkspaceRole): string {
  if (!role) return 'bg-slate-50 text-slate-500 border-slate-200';
  return ROLE_COLORS[role] || 'bg-slate-50 text-slate-500 border-slate-200';
}

export function derivePermissions(role: WorkspaceRole): Omit<WorkspacePermissions, 'isLoading'> {
  return {
    role,
    canEdit: role === 'admin' || role === 'delivery_lead',
    canManageTeam: role === 'admin',
    canViewAll: role !== null,
    canUpdateProject: role === 'admin' || role === 'delivery_lead' || role === 'pmo',
  };
}

export function useWorkspaceRole(transformationId?: string): WorkspacePermissions {
  const { user } = useAuth();
  const [role, setRole] = useState<WorkspaceRole>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRole = useCallback(async () => {
    if (!user || !transformationId) {
      setIsLoading(false);
      return;
    }
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('workspace_members')
        .select('workspace_role')
        .eq('transformation_id', transformationId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        // Fall back to org-level role from user_profiles
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (profile?.role === 'admin') {
          setRole('admin');
        } else if (profile?.role === 'adoption_lead') {
          setRole('delivery_lead');
        } else {
          setRole('viewer');
        }
      } else {
        setRole(data.workspace_role as WorkspaceRole);
      }
    } catch {
      setRole('viewer');
    } finally {
      setIsLoading(false);
    }
  }, [user, transformationId]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  return {
    ...derivePermissions(role),
    isLoading,
  };
}

// Hook to get role for the first/active transformation in the user's org
export function useActiveWorkspaceRole(): WorkspacePermissions {
  const { user } = useAuth();
  const [transformationId, setTransformationId] = useState<string | undefined>(undefined);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    const fetchTx = async () => {
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', user.id)
          .single();
        if (!profile?.organization_id) { setResolved(true); return; }

        const { data: tx } = await supabase
          .from('transformations')
          .select('id')
          .eq('organization_id', profile.organization_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (tx?.id) setTransformationId(tx.id);
      } catch { /* silent */ } finally {
        setResolved(true);
      }
    };
    fetchTx();
  }, [user]);

  const roleResult = useWorkspaceRole(resolved ? transformationId : undefined);
  return roleResult;
}
